<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import DatePicker from 'dashboard/components/carBooking/DatePicker.vue';

const props = defineProps({
  // The advanced filters only: `q`, booking type and status stay in the header
  // and are never cleared by this component.
  filters: { type: Object, required: true },
  options: { type: Object, required: true },
  isOpen: { type: Boolean, default: false },
});

const emit = defineEmits(['update', 'clear']);

const { t } = useI18n();

const KEY = 'BOOKINGS_PAGE.FILTERS';

const SOURCES = ['manual', 'travel_router'];

const capitalize = value =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : '';

const stationLabel = station =>
  station.name ? `${station.code} · ${station.name}` : station.code;

const providerOptions = computed(() => [
  { value: '', label: t(`${KEY}.ALL_PROVIDERS`) },
  ...(props.options.providers || []).map(provider => ({
    value: provider,
    label: capitalize(provider),
  })),
]);

const ratePlanOptions = computed(() => [
  { value: '', label: t(`${KEY}.ALL_RATE_PLANS`) },
  ...(props.options.rate_plans || []).map(plan => ({
    value: plan,
    label: plan,
  })),
]);

const stationOptions = computed(() => [
  { value: '', label: t(`${KEY}.ANY_STATION`) },
  ...(props.options.stations || []).map(station => ({
    value: station.code,
    label: stationLabel(station),
  })),
]);

const sourceOptions = computed(() => [
  { value: '', label: t(`${KEY}.ALL_SOURCES`) },
  ...SOURCES.map(source => ({
    value: source,
    label: t(`${KEY}.SOURCE_${source.toUpperCase()}`),
  })),
]);

const agentOptions = computed(() => [
  { value: '', label: t(`${KEY}.ANY_AGENT`) },
  ...(props.options.agents || []).map(agent => ({
    value: String(agent.id),
    label: agent.name,
  })),
]);

const labelFor = (options, value) =>
  options.find(option => option.value === value)?.label || value;

// One chip per applied filter, in the same order as the filter bar so the
// collapsed summary reads like the expanded form. The displayed text comes from
// the matching option, but a chip only appears when the filter itself is set —
// the empty option's label ("All providers") would otherwise read as a value.
const chips = computed(() => {
  const { filters } = props;
  const entries = [
    ['provider', t(`${KEY}.PROVIDER`), providerOptions.value],
    ['ratePlan', t(`${KEY}.RATE_PLAN`), ratePlanOptions.value],
    ['station', t(`${KEY}.STATION`), stationOptions.value],
    ['dateFrom', t(`${KEY}.DATE_FROM`), null],
    ['dateTo', t(`${KEY}.DATE_TO`), null],
    ['source', t(`${KEY}.SOURCE`), sourceOptions.value],
    ['userId', t(`${KEY}.AGENT`), agentOptions.value],
    ['oneWay', t(`${KEY}.ONE_WAY`), null],
  ];
  return entries
    .filter(([name]) => Boolean(filters[name]))
    .map(([name, label, options]) => ({
      name,
      label,
      value: options
        ? labelFor(options, filters[name])
        : (filters[name] === true && t(`${KEY}.ONE_WAY_ON`)) || filters[name],
    }));
});

const remove = name =>
  emit('update', { [name]: name === 'oneWay' ? false : '' });
</script>

<template>
  <div
    v-if="isOpen"
    class="grid gap-3 p-4 rounded-xl bg-n-alpha-2 dark:bg-n-solid-1 sm:grid-cols-2 lg:grid-cols-4"
  >
    <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-11">
      {{ t(`${KEY}.PROVIDER`) }}
      <Select
        :model-value="filters.provider"
        :options="providerOptions"
        class="!w-full"
        @update:model-value="emit('update', { provider: $event })"
      />
    </label>

    <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-11">
      {{ t(`${KEY}.RATE_PLAN`) }}
      <Select
        :model-value="filters.ratePlan"
        :options="ratePlanOptions"
        class="!w-full"
        @update:model-value="emit('update', { ratePlan: $event })"
      />
    </label>

    <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-11">
      {{ t(`${KEY}.STATION`) }}
      <Select
        :model-value="filters.station"
        :options="stationOptions"
        class="!w-full"
        @update:model-value="emit('update', { station: $event })"
      />
    </label>

    <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-11">
      {{ t(`${KEY}.SOURCE`) }}
      <Select
        :model-value="filters.source"
        :options="sourceOptions"
        class="!w-full"
        @update:model-value="emit('update', { source: $event })"
      />
    </label>

    <div class="flex flex-col gap-1 text-xs font-medium text-n-slate-11">
      {{ t(`${KEY}.DATE_FROM`) }}
      <DatePicker
        :model-value="filters.dateFrom"
        :placeholder="t(`${KEY}.ANY_DATE`)"
        @update:model-value="emit('update', { dateFrom: $event })"
      />
    </div>

    <div class="flex flex-col gap-1 text-xs font-medium text-n-slate-11">
      {{ t(`${KEY}.DATE_TO`) }}
      <DatePicker
        :model-value="filters.dateTo"
        :min="filters.dateFrom"
        :placeholder="t(`${KEY}.ANY_DATE`)"
        @update:model-value="emit('update', { dateTo: $event })"
      />
    </div>

    <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-11">
      {{ t(`${KEY}.AGENT`) }}
      <Select
        :model-value="filters.userId"
        :options="agentOptions"
        class="!w-full"
        @update:model-value="emit('update', { userId: $event })"
      />
    </label>

    <div class="flex items-end justify-between gap-2">
      <span class="flex items-center h-10 gap-2 text-sm text-n-slate-12">
        <Switch
          :model-value="filters.oneWay"
          @update:model-value="emit('update', { oneWay: $event })"
        />
        {{ t(`${KEY}.ONE_WAY`) }}
      </span>
      <Button
        link
        slate
        sm
        :label="t(`${KEY}.CLEAR`)"
        class="h-10"
        @click="emit('clear')"
      />
    </div>
  </div>

  <div v-else-if="chips.length" class="flex flex-wrap items-center gap-2">
    <span
      v-for="chip in chips"
      :key="chip.name"
      class="inline-flex items-center gap-1.5 rounded-full bg-n-alpha-2 dark:bg-n-solid-1 py-1 ltr:pl-3 ltr:pr-2 rtl:pr-3 rtl:pl-2 text-xs text-n-slate-12"
    >
      <span class="text-n-slate-11">
        {{ t(`${KEY}.CHIP_LABEL`, { label: chip.label }) }}
      </span>
      {{ chip.value }}
      <button
        type="button"
        class="flex items-center justify-center rounded-full size-4 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
        :aria-label="t(`${KEY}.REMOVE`, { label: chip.label })"
        @click="remove(chip.name)"
      >
        <span class="i-lucide-x size-3" />
      </button>
    </span>
    <Button link slate sm :label="t(`${KEY}.CLEAR`)" @click="emit('clear')" />
  </div>
</template>
